'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SeatData } from '@/components/grid';

/**
 * Resolve the bulk-action category a given seat falls into.
 *
 * Mirrors `useMultiSelect.getSeatMode` (`@/hooks/use-multi-select.ts:103-109`)
 * — kept in sync byte-for-byte so the basket and the bulk-action drawer
 * agree on which mode each seat belongs to. Forfeit-pending takes
 * precedence over ownership: an owned seat whose deposit has decayed to
 * zero has nothing left to "manage", and the next contract transition on
 * it is the forfeit itself, which any caller registers via `pokeTax` —
 * routing those seats to `'poke'` keeps the basket category coherent.
 */
/**
 * Bulk-action category resolved per seat by `getBasketSeatMode`.
 * Mirrors `useMultiSelect.BulkMode` byte-for-byte (including the
 * `'manage-creator'` branch) so the basket and the bulk drawer agree
 * on which tiles can co-exist in a single basket. See
 * `use-multi-select.ts::BulkMode` JSDoc for the full taxonomy.
 */
export type BulkMode = 'manage' | 'manage-creator' | 'poke' | 'claim' | 'buyout';

export function getBasketSeatMode(seat: SeatData): BulkMode {
  if (seat.isReserved) return 'claim';
  if (seat.isPendingForfeit) return 'poke';
  // Creator inventory (viewer == grid creator, holder == viewer,
  // deposit == 0n, pre-graduation) is its own basket category. This
  // is what stops a creator from accidentally mixing their primary-
  // mint inventory with seats they later bought back from a third
  // party — those two cohorts have completely different action
  // surfaces (creator inventory has no Harberger deposit to manage,
  // bought-back seats do) and the bulk drawer can't render them in
  // the same body without misleading the user. The bulk drawer
  // routes this mode to a "share grid link" notice mirroring the
  // single-tile BuyOutDrawer's creator branch.
  if (seat.isOwn && seat.isCreatorSeat) return 'manage-creator';
  if (seat.isOwn) return 'manage';
  if (!seat.holder || seat.isForfeited || seat.isCreatorSeat) return 'claim';
  return 'buyout';
}

/**
 * `true` iff the seat is currently sitting inside an active V22 Dutch
 * auction window — i.e. the grid uses Dutch decay, the seat is vacant
 * (no holder or forfeited), and `antiSnipeEndsAt` is still in the
 * future. Held seats on Dutch grids and post-window-expiry vacant
 * seats (free-claim phase) both return `false`.
 *
 * Bulk Dutch flows would surface a per-seat decay clock × N rows in
 * the bulk drawer with mismatched clearing prices and refund maths,
 * which the user explicitly called out as too confusing. The basket
 * refuses to add seats matching this predicate (see `toggle` below) so
 * Dutch buyouts always go through the single-tile `BuyOutDrawer`
 * routed by `LobbyClient#openDrawerForSeat`. Every other tile on the
 * same Dutch grid (held seats, expired-window vacant seats) remains
 * freely multi-selectable.
 */
export function isSeatInActiveDutchAuction(seat: SeatData): boolean {
  if (!seat.gridUsesDutchAuction) return false;
  if (seat.holder && !seat.isForfeited) return false;
  if (!seat.antiSnipeEndsAt) return false;
  const nowSec = Math.floor(Date.now() / 1000);
  return seat.antiSnipeEndsAt > nowSec;
}

/**
 * Lobby-local multi-select "basket" for Cmd/Ctrl+click checkout flow.
 *
 * Feature scope (matches the classic app's `useMultiSelect` hook at a high
 * level but intentionally trimmed for the lobby preview):
 *   - Cmd/Ctrl+click a tile → toggle it in the basket
 *   - When the basket is non-empty, any click (with or without the modifier)
 *     toggles membership — the user stays in "basket mode" until the basket
 *     is cleared (Esc / Clear / Checkout).
 *   - The basket is **single-mode**: once a seat is added, only seats
 *     resolving to the same `BulkMode` (manage / buyout / claim / poke)
 *     are accepted by `toggle`. Mismatched clicks no-op so a user can't
 *     end up with a basket that mixes Manage and Buyout (the user-
 *     reported regression). The mode resets back to `null` whenever the
 *     basket empties (clear / last toggle removes the only entry).
 *   - Tiles that match `isSeatInActiveDutchAuction` are silently
 *     refused by `toggle`. Single-tile Dutch buyouts continue to work
 *     through the standard `LobbyClient#openDrawerForSeat` route —
 *     this just keeps them out of the multi-select basket. Every
 *     other tile on the same Dutch grid (held seats, expired-window
 *     vacant seats) is still freely multi-selectable.
 *
 * Note: we listen for both `Meta` (⌘ on macOS) and `Control` (Ctrl on
 * Windows/Linux) so the hint state lights up for either platform convention.
 * Shift is intentionally NOT a trigger — it's reserved for the camera's
 * drop-down control.
 */
export interface LobbyBasket {
  /** Seat indices currently in the basket. */
  selectedIndices: Set<number>;
  /** Active bulk-action category, or `null` while the basket is empty.
   *  Drives the lobby-canvas dim/focus overlay (non-matching tiles drop
   *  to ~30% opacity) and gates the bulk-action drawer's mode. */
  bulkMode: BulkMode | null;
  /** True while the user is actively holding Cmd (macOS) or Ctrl
   *  (Windows/Linux). Drives hover-time cursor / outline hints even before
   *  the first toggle. */
  isModifierHeld: boolean;
  /** Add or remove a seat. Toggling a seat whose mode doesn't match the
   *  current basket mode is a no-op — the basket is single-mode by
   *  construction. Adds for tiles in an active Dutch auction window
   *  are also refused (single-tile buyout only). The boolean
   *  return is `true` when the basket changed, `false` when the
   *  click was rejected (handy for the canvas's "click feedback"
   *  hooks that should suppress the toggle ping when we ignored
   *  the click). */
  toggle: (seat: SeatData) => boolean;
  /** Remove a single seat from the basket (no-op if absent). */
  remove: (seatIndex: number) => void;
  /** Empty the basket. Called by Esc, Clear button, post-checkout, and
   *  on every board-change so a stale basket never bleeds into the
   *  next board's first click. */
  clear: () => void;
  /** Pure helper: which mode would this seat fall into? Re-exported so
   *  the canvases can compute "is this tile dimmed / selectable?"
   *  without duplicating the precedence rules. */
  getSeatMode: (seat: SeatData) => BulkMode;
  /** `true` when the seat is either the basket's first entry (any mode
   *  is accepted) or matches the basket's current mode. Mirrors the
   *  classic `useMultiSelect.isSeatSelectable` semantics. Returns
   *  `false` for tiles that are currently in an active Dutch auction
   *  window — those open through the single-tile buyout drawer
   *  instead. */
  isSeatSelectable: (seat: SeatData) => boolean;
}

export function useLobbyBasket(): LobbyBasket {
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(
    () => new Set<number>(),
  );
  const [bulkMode, setBulkMode] = useState<BulkMode | null>(null);
  const [isModifierHeld, setIsModifierHeld] = useState(false);

  useEffect(() => {
    const isBasketMod = (e: KeyboardEvent) =>
      e.key === 'Meta' || e.key === 'Control';
    const onKeyDown = (e: KeyboardEvent) => {
      if (isBasketMod(e)) setIsModifierHeld(true);
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (isBasketMod(e)) setIsModifierHeld(false);
    };
    // If the user tabs away or the window loses focus we might miss the
    // keyup (particularly on macOS where Cmd+Tab eats the event).
    const onBlur = () => setIsModifierHeld(false);

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  const toggle = useCallback((seat: SeatData): boolean => {
    const seatMode = getBasketSeatMode(seat);
    let changed = false;
    setSelectedIndices((prev) => {
      // Removal path: always allowed, regardless of current mode (a
      // non-matching seat couldn't have entered the basket anyway, but
      // we don't want a stale-mode click to leak past the membership
      // check). Also bypasses the Dutch-auction refusal below so an
      // already-basketed tile can still be removed even if its window
      // expired mid-session.
      if (prev.has(seat.seatIndex)) {
        const next = new Set(prev);
        next.delete(seat.seatIndex);
        if (next.size === 0) setBulkMode(null);
        changed = true;
        return next;
      }

      // Addition path: first pick sets the mode; subsequent picks must
      // match. Mismatched clicks no-op (return `prev` so React skips
      // the re-render entirely).
      if (bulkMode != null && seatMode !== bulkMode) {
        return prev;
      }
      // Tiles inside an active Dutch auction window are not
      // basket-eligible — the per-seat decay clock + clearing-price
      // maths don't compose into a bulk drawer. The single-tile
      // buyout drawer (`BuyOutDrawer` via `openDrawerForSeat`)
      // handles those. Every other tile on the same Dutch grid
      // (held seats for buyout, expired-window vacant seats for
      // claim) stays freely multi-selectable.
      if (isSeatInActiveDutchAuction(seat)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(seat.seatIndex);
      if (bulkMode == null) setBulkMode(seatMode);
      changed = true;
      return next;
    });
    return changed;
  }, [bulkMode]);

  const remove = useCallback((seatIndex: number) => {
    setSelectedIndices((prev) => {
      if (!prev.has(seatIndex)) return prev;
      const next = new Set(prev);
      next.delete(seatIndex);
      if (next.size === 0) setBulkMode(null);
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setSelectedIndices((prev) => (prev.size === 0 ? prev : new Set<number>()));
    setBulkMode(null);
  }, []);

  const getSeatMode = useCallback(
    (seat: SeatData): BulkMode => getBasketSeatMode(seat),
    [],
  );

  const isSeatSelectable = useCallback(
    (seat: SeatData): boolean => {
      // Already-selected tiles always read as selectable so the
      // toggle's removal path keeps registering even if the seat's
      // Dutch window opens up after it was added.
      if (selectedIndices.has(seat.seatIndex)) return true;
      if (isSeatInActiveDutchAuction(seat)) return false;
      return bulkMode == null || getBasketSeatMode(seat) === bulkMode;
    },
    [bulkMode, selectedIndices],
  );

  return useMemo(
    () => ({
      selectedIndices,
      bulkMode,
      isModifierHeld,
      toggle,
      remove,
      clear,
      getSeatMode,
      isSeatSelectable,
    }),
    [
      selectedIndices,
      bulkMode,
      isModifierHeld,
      toggle,
      remove,
      clear,
      getSeatMode,
      isSeatSelectable,
    ],
  );
}
